import React, { useEffect, useMemo, useRef, useState } from "react";
import Popup from "./Popup";
import {
  DATE_TIME_FORMAT,
  reportStatus as reportStatusList,
  reportStatusColor,
  reportStatusLabel,
  fileNameType,
  caseStatus,
} from "app/constants";
import moment from "moment";
import { commonServices, projectServices } from "app/services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import PrimaryBtn from "../../../UI/Button/PrimaryBtn";
import { toast } from "react-toastify";
import messages from "../../../../services/api/messages";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import InputText from "../../../UI/Form/Input/InputText";
import { useFormik } from "formik";
import { formatIndex, getBaseName, getFileExtension } from "../../../../utils";

function ReportCell(props) {
  const {
    width,
    reportStatus,
    reportInfo,
    caseInfo,
    setCaseInfo,
    highlight = false,
    setDownloading,
  } = props;
  const [anchor, setAnchor] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const ref = useRef();
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (highlight) {
      ref?.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight]);

  const disabledChangeStatus = useMemo(() => {
    return (
      (caseInfo?.caseStatus === caseStatus.completed ||
        caseInfo?.caseStatus === caseStatus.lost) &&
      reportStatus !== reportStatusList.submitted
    );
  }, [caseInfo, reportStatus]);

  const handleLeftClickReport = () => {
    if (reportInfo?.fileInfo) {
      const caseId = caseInfo?.caseId || "";
      const fileType = fileNameType["report"];
      const customerName = caseInfo?.customer?.companyNameKanji || "";
      const timeStamp = moment().format("YYYYMMDDHHmmss");

      const files = Array.isArray(reportInfo.fileInfo) ? reportInfo.fileInfo : [reportInfo.fileInfo];

      setDownloading(true);

      Promise.all(
        files.map((file, index) =>
          new Promise((resolve) => {
            setTimeout(() => {
              const name = file.fileName
                ? getBaseName(file.fileName)
                : `${fileType}_${caseId}_${customerName}_${formatIndex(index + 1)}_${timeStamp}`;

              const extension = getFileExtension(file);
              if (extension === "zip") {
                commonServices
                  .downloadWithFileId(file._id, extension, name)
                  .finally(() => resolve());
              } else {
                commonServices
                  .downloadPDFWithFileId(file._id, name)
                  .finally(() => resolve());
              }
            }, index * 500);
          })
        )
      ).finally(() => setDownloading(false));
    }
  };

  const handleRightClickReport = (e) => {
    e.preventDefault();
    if (disabledChangeStatus) return;
    if (
      reportStatus === reportStatusList.confirmed ||
      reportStatus === reportStatusList.submitted
    )
      setAnchor(e.target);
  };

  const handleUpdateReportStatus = (newStatus) => {
    if (isUpdating || reportStatus !== reportStatusList.confirmed) return;

    setIsUpdating(true);
    projectServices
      .updateStatus({
        id: caseInfo._id,
        statusType: "reportStatus",
        value: values.comment ? reportStatusList.rejected : newStatus,
        comment: values.comment,
      })
      .then((res) => {
        const newCaseInfo = res?.data?.result;
        if (!newCaseInfo._id) throw Error("Update error!");

        setIsUpdating(false);
        setAnchor(null);
        setOpenModal(false);
        setCaseInfo({
          ...caseInfo,
          reportStatus: newCaseInfo?.reportStatus,
          caseStatus: newCaseInfo?.caseStatus,
          updateReportTime: newCaseInfo?.updateReportTime,
        });
      })
      .catch(() => {
        toast.error(messages.callApiFail);
        setIsUpdating(false);
      });
  };

  const showModal = () => {
    setOpenModal(true);
    setAnchor(null);
    resetForm();
  };

  const closeModal = () => {
    setOpenModal(false);
    setAnchor(null);
  };

  const {
    handleSubmit,
    resetForm,
    errors,
    values,
    setFieldValue,
    touched,
    handleBlur,
  } = useFormik({
    initialValues: {
      comment: "",
    },
    onSubmit: handleUpdateReportStatus,
    enableReinitialize: true,
  });

  return (
    <>
      <div
        ref={ref}
        className={`h-full border border-[#B3B3B3] px-2 py-4 shrink-0 ${
          highlight && "bg-yellow-100"
        }`}
        style={{
          width: width,
        }}
      >
        <p className="text-sm leading-[18px] tracking-[.2em] truncate text-center">
          <button
            style={{
              color: reportStatusColor[reportStatus],
              cursor: "pointer",
            }}
            onClick={handleLeftClickReport}
            onContextMenu={handleRightClickReport}
          >
            {reportStatusLabel[reportStatus]}
          </button>
        </p>
      </div>

      <Popup
        id={`request-form-${reportInfo?._id}`}
        anchor={anchor}
        open={Boolean(anchor)}
      >
        {reportStatus === reportStatusList.confirmed &&
        !disabledChangeStatus ? (
          <div className="flex flex-col gap-2">
            <p className="flex gap-2">
              最終更新日:{" "}
              {moment(
                caseInfo?.updateReportTime || reportInfo?.updatedAt
              ).format(DATE_TIME_FORMAT)}
            </p>
            <div className="flex gap-2">
              <PrimaryBtn
                text="承認"
                onClick={() =>
                  handleUpdateReportStatus(reportStatusList.submitted)
                }
              />
              <PrimaryBtn text="差し戻し" onClick={showModal} />
              <PrimaryBtn text="戻る" onClick={() => setAnchor(null)} />
            </div>
          </div>
        ) : null}

        {reportStatus === reportStatusList.submitted ? (
          <div className="flex flex-col">
            <button
              className="bg-[#c7c2c2] w-7 h-7 rounded-full ml-auto flex items-center justify-center"
              onClick={() => setAnchor(null)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <p className="flex gap-2">
              最終更新日:{" "}
              {moment(
                caseInfo?.updateReportTime || reportInfo?.updatedAt
              ).format(DATE_TIME_FORMAT)}
            </p>
          </div>
        ) : null}
      </Popup>

      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogContent
          sx={{
            pl: "16px",
          }}
        >
          <div className="mb-4">コメント</div>
          <InputText
            name="comment"
            value={values.comment}
            onChange={(value) => setFieldValue("comment", value)}
            handleBlur={handleBlur}
            errors={errors.comment}
            touched={touched.comment}
          />
        </DialogContent>

        <DialogActions>
          <PrimaryBtn text={"キャンセル"} onClick={closeModal} />
          <PrimaryBtn text={"送信"} onClick={handleSubmit} />
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ReportCell;
